import { useMemo, useState } from 'react';

import { useUpdateVenueMutation } from '@admin/mutations/VenuesMutations';

import { Series, useSeriesByTagId } from '@queries/TagQueries';
import { SuperAdminVenue, useCityVenuesQuery } from '@queries/VenueQueries';

import { Input } from '@components/form/Input';
import { Toggle } from '@components/form/Toggle';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

const VenueItem = ({ venue, onUpdate }: { venue: SuperAdminVenue; onUpdate: () => void }) => {
  const [isVerified, setIsVerified] = useState(venue.isVerified);
  const { updateVenueAsync } = useUpdateVenueMutation();

  const subtitle = useMemo(() => {
    const details = [];

    if (venue.contact.instagram) {
      details.push(`@${venue.contact.instagram}`);
    }

    return details.join(' | ');
  }, [venue]);

  const handleVerifiedChange = async (updatedValue: boolean) => {
    setIsVerified(updatedValue);
    await updateVenueAsync({ _id: venue._id, isVerified: updatedValue });
    onUpdate();
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between">
        <div>
          <div>
            <strong>
              {venue.title}
              {venue.contact.googlePlaceId ? ' 📍' : ''}
            </strong>
          </div>
          {subtitle && <div style={{ fontSize: '.8rem', opacity: 0.8 }}>{subtitle}</div>}
        </div>
        <div>
          <Toggle isChecked={isVerified} onChange={handleVerifiedChange} />
        </div>
      </div>
    </div>
  );
};

export const SeriesVenuesSection = ({ tagId }: { tagId: string }) => {
  const { series } = useSeriesByTagId(tagId);
  const { refetchCityVenues, cityVenues = [] } = useCityVenuesQuery(series?.city?._id);
  const [searchValue, setSearchValue] = useState('');

  const verifiedVenues = useMemo(
    () => cityVenues?.filter((venue) => venue.isVerified),
    [cityVenues],
  );

  const filteredVenues = useMemo(() => {
    if (!searchValue) {
      return [];
    }

    const searchValueLower = searchValue.toLowerCase().trim();

    return cityVenues.filter((venue) => venue.title.toLowerCase().includes(searchValueLower));
  }, [searchValue, cityVenues]);

  return (
    <>
      <TitleToolbar
        text="Venues"
        size="md"
        action={<>{`${verifiedVenues.length} verified venues`}</>}
      />
      <div>
        <Input onChange={(e) => setSearchValue(e.target.value)} />
      </div>
      <div className="d-grid gap-2 mt-3">
        {filteredVenues.map((venue) => (
          <VenueItem key={venue._id} venue={venue} onUpdate={() => refetchCityVenues()} />
        ))}
      </div>
    </>
  );
};
